import React, { useCallback, useEffect, useRef, useState } from "react";
import { map, mergeRight } from "ramda";

import PanelLoadingIndicator from "components/PanelLoadingIndicator";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import makeStyles from "@mui/styles/makeStyles";
import { grey } from "@mui/material/colors";
import { useDispatch, useSelector } from "react-redux";
import { FetchError, fetchSecurityUser } from "util/fetchUtils";
import { logOut } from "auth/authActions";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import SipServerListItem from "security-settings/sip-servers/components/SipServerListItem";
import EditSipServer from "security-settings/sip-servers/components/EditSipServer";
import ConfirmDeleteDialog from "security-settings/sip-servers/components/ConfirmDeleteDialog";
import AddIcon from "@mui/icons-material/Add";
import { Container, Paper } from "@mui/material";
import { notBlank } from "../../../util/ext";
import { useDeleteSipServer, useSipServers } from "../sipServerServices";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    panel: {
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    listPaper: {
      width: "100%",
    },

    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: 60,
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      padding: theme.spacing(4),
    },

    leftContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      minWidth: 200,
    },

    rightContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      flexGrow: 1,
    },

    title: {
      flexGrow: 1,
      display: "flex",
      alignItems: "center",
      fontSize: "2.0rem",
      fontWeight: "bold",
    },

    newButton: {
      textTransform: "uppercase",
      color: "#127DA9",
      fontSize: "1.2rem",
      fontWeight: "bold",
    },

    listContainer: {
      height: "calc(100vh - 350px)",
      overflow: "auto",
    },

    list: {
      height: "100%",
    },

    listItem: {
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderBottomColor: grey[300],
    },

    creatorLogo: {
      width: "100%",
      height: "100%",
    },
  }));

const SipServerBrowse = () => {
  const [scrollVisible, setScrollVisible] = useState(true);

  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();

  const [servers, setServers] = useState([]);
  const [serversLoaded, setServersLoaded] = useState(false);
  const [userCache, setUserCache] = useState(new Map());
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [currentServer, setCurrentServer] = useState({});
  const [errorAlert, setErrorAlert] = useState({ open: false });
  const classes = useStyles(scrollVisible)();

  const handleEditDialogOnCancel = () => {
    setEditDialogVisible(false);
  };

  const handleEditDialogOnSave = server => {
    setCurrentServer(server);
    setEditDialogVisible(false);
  };

  const handleAddServerOnClick = () => {
    setCurrentServer({});
    setEditDialogVisible(true);
  };

  const handleEditServerOnClick = server => {
    setCurrentServer(server);
    setEditDialogVisible(true);
  };

  const handleDeleteServerOnClick = server => {
    setCurrentServer(server);
    setDeleteConfirmVisible(true);
  };

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const handleError = useCallback(
    message => err => {
      if (err instanceof FetchError) {
        if (err.status === 401) {
          dispatch(logOut());
        } else {
          showAlert(message);
        }
      } else {
        showAlert(err.message);
      }
    },
    [dispatch],
  );

  const deleteSipServer = useDeleteSipServer();
  const handleDeleteServerOk = async server => {
    try {
      setDeleteConfirmVisible(false);
      await deleteSipServer.mutateAsync({
        resourceIds: {
          sipServerId: server.sipServerId,
        },
      });
    } catch (err) {
      handleError("Error Deleting Sip Server. Server my be in use.")(err);
    }
  };

  const lookupUser = useCallback(
    async userId => {
      if (!userCache.has(userId)) {
        const user = await fetchSecurityUser(auth)(userId);
        setUserCache(uc => uc.set(userId, user));
      }

      return userCache.get(userId);
    },
    [auth, userCache],
  );

  const sipServers = useSipServers();
  useEffect(() => {
    const loadSipServers = async servers => {
      try {
        const serverFull = await Promise.all(
          map(async srv => {
            const [createdBy, updatedBy] = await Promise.all([
              lookupUser(srv.createdBy),
              lookupUser(srv.updatedBy),
            ]);
            srv.createdUser = createdBy;
            srv.updatedUser = updatedBy;
            return srv;
          }, servers),
        );

        setServers(serverFull);
        setServersLoaded(true);
      } catch (err) {
        handleError("Error fetching Sip Servers.")(err);
      }
    };

    if (sipServers.isSuccess) {
      loadSipServers(sipServers.data);
      setScrollVisible(true);
    }
  }, [auth, lookupUser, handleError, sipServers.isSuccess, sipServers.data]);

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl) && notBlank(tableContainerEl.current)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      setScrollVisible(scrollVisible);
    }
  }, [tableContainerEl]);

  return (
    <Container
      id="sip-server-browse"
      maxWidth={false}
      classes={{ root: classes.panel }}
    >
      <EditSipServer
        open={editDialogVisible}
        server={currentServer}
        onSave={handleEditDialogOnSave}
        onClose={handleEditDialogOnCancel}
      />

      <ConfirmDeleteDialog
        open={deleteConfirmVisible}
        server={currentServer}
        onCancel={() => setDeleteConfirmVisible(false)}
        onOk={handleDeleteServerOk}
      />

      <div>
        <div>
          <div className={classes.toolbar}>
            <div className={classes.leftContainer}>
              <div className={classes.title}>SIP Servers</div>
            </div>

            <div>
              <div>
                <Button
                  variant={"text"}
                  startIcon={<AddIcon />}
                  classes={{ root: classes.newButton }}
                  onClick={handleAddServerOnClick}
                >
                  New SIP Server
                </Button>
              </div>
            </div>
          </div>
        </div>

        {serversLoaded ? (
          <Container maxWidth={false} classes={{ root: classes.panel }}>
            <Paper elevation={0} className={classes.listPaper}>
              <Container
                maxWidth={false}
                ref={tableContainerEl}
                className={classes.listContainer}
              >
                <List
                  classes={{
                    root: classes.list,
                  }}
                >
                  {servers.map(server => {
                    const selected =
                      server.sipServerId === currentServer.sipServerId ||
                      server.hostname === currentServer.hostname;
                    return (
                      <SipServerListItem
                        key={server.sipServerId}
                        server={server}
                        selected={selected}
                        onEditClick={handleEditServerOnClick}
                        onDeleteClick={handleDeleteServerOnClick}
                      />
                    );
                  })}
                </List>
              </Container>
            </Paper>
          </Container>
        ) : (
          <PanelLoadingIndicator />
        )}
      </div>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SipServerBrowse;
